import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import Img from "@/components/Img";
import { APPLICATIONS, BRANDS, APP_IDS } from "@/data/content";
import { useLanguage } from "@/i18n/LanguageContext";

const CATS = APP_IDS;

export default function Aplicacoes() {
  const { t, lp } = useLanguage();
  const [filter, setFilter] = useState(null);

  const visible = useMemo(
    () => (filter ? APPLICATIONS.filter((a) => a.id === filter) : APPLICATIONS),
    [filter]
  );

  return (
    <div data-testid="applications-page" className="pt-20">
      <Seo title={t("seo.applications.title")} description={t("seo.applications.desc")} />

      <section className="pt-16 lg:pt-20 pb-20 max-w-7xl mx-auto px-6">
        <Reveal className="mb-8 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:items-end">
          <div className="lg:col-span-7">
            <span className="mono-tag text-xs uppercase tracking-[0.22em] text-[#C2A675]">{t("apps_page.tag")}</span>
            <h1 className="serif-display text-4xl sm:text-5xl lg:text-6xl font-light text-white mt-4">{t("apps_page.title")}</h1>
          </div>
          <p className="lg:col-span-5 text-base text-[#A8A6A0] font-light leading-relaxed">{t("apps_page.sub")}</p>
        </Reveal>

        <Reveal delay={0.1} className="flex flex-wrap gap-3 mb-10 border-y border-white/10 py-5" data-testid="app-filters">
          <button
            type="button"
            onClick={() => setFilter(null)}
            data-testid="app-filter-all"
            className={`px-4 py-2 text-[11px] uppercase tracking-[0.14em] border transition-colors ${!filter ? "border-[#C2A675] text-[#C2A675]" : "border-white/15 text-[#A8A6A0] hover:border-white/40 hover:text-white"}`}
          >
            {t("common.all")}
          </button>
          {CATS.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setFilter(filter === c ? null : c)}
              data-testid={`app-filter-${c}`}
              className={`px-4 py-2 text-[11px] uppercase tracking-[0.14em] border transition-colors ${filter === c ? "border-[#C2A675] text-[#C2A675]" : "border-white/15 text-[#A8A6A0] hover:border-white/40 hover:text-white"}`}
            >
              {t(`app_cats.${c}`)}
            </button>
          ))}
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {visible.map((a, i) => (
            <Reveal key={a.id} delay={(i % 3) * 0.08}>
              <article className="img-frame group aspect-[4/3] border border-white/10" data-testid={`app-card-${a.id}`}>
                <Img id={a.image} alt={t(`app_items.${a.id}`)} label={t("common.pending_image")} className="absolute inset-0 w-full h-full object-cover img-tone" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent p-6 flex flex-col justify-end">
                  <span className="mono-tag text-[10px] text-[#C2A675] uppercase tracking-[0.2em]">{t(`app_cats.${a.id}`)}</span>
                  <h2 className="serif-display text-white text-2xl font-light mt-2 leading-tight">{t(`app_items.${a.id}`)}</h2>
                  {a.brands && <p className="text-[11px] uppercase tracking-[0.14em] text-[#D6D3CC] mt-2">{a.brands}</p>}
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        {filter && (
          <div className="mt-10 border border-white/10 bg-[#16181D] p-6 sm:p-8" data-testid="apps-brands-for">
            <p className="mono-tag text-[10px] uppercase tracking-[0.22em] text-[#C2A675] mb-4">{t("apps_page.brands_for")} — {t(`app_cats.${filter}`)}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {BRANDS.filter((b) => b.apps.includes(filter)).map((b) => (
                <Link key={b.slug} to={lp(`/marcas/${b.slug}`)} data-testid={`apps-brand-${b.slug}`} className="group flex items-center gap-4 border border-white/10 p-3 hover:border-[#C2A675]/50 transition-colors">
                  <div className="img-frame w-20 h-16 shrink-0"><Img id={b.image} alt={b.name} label={t("common.pending_image")} small className="absolute inset-0 w-full h-full object-cover img-tone" /></div>
                  <div className="min-w-0">
                    <p className="serif-display text-xl text-white font-light">{b.name}</p>
                    <p className="text-[10px] uppercase tracking-[0.16em] text-[#A8A6A0] truncate">{t(`brand.${b.slug}.tag`)}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        <Reveal className="mt-14 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 border-t border-white/10 pt-8">
          <div className="max-w-lg">
            {t("home.cta_text").map((p, i) => (
              <p key={i} className={`text-base text-[#A8A6A0] font-light leading-relaxed ${i > 0 ? "mt-2" : ""}`}>{p}</p>
            ))}
          </div>
          <Link to={lp("/orcamento")} data-testid="apps-quote-cta" className="btn-solid px-8 py-4 shrink-0">
            {t("common.request_quote")}
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
